import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { formatISO } from 'date-fns';
import {
  AllocateOrderInventoryCompletedMessage,
  FileImportCompleteMessage,
} from '../../types/signalRTypes';
import {
  InventoryPool,
  OrderItemAPI,
  OrderItemScanResponse,
  OrderPhysicalItemAPI,
  ProcessingPackagesState,
  ProcessOrderAPI,
} from '../../types/orderTypes';

export interface ReshipState {
  // flags if the default values were overridden
  hasOverrideCarrierInfo: boolean;
  hasOverrideShipToInfo: boolean;
  reshipReturnReason: string;
  overrideShouldChargeToCustomer: boolean;
  warehouseID: string | number;
  carrierRequestedName: string;
  carrierID: string | number;
  carrierServiceCode: string;
  fulfillmentType: string;
  shippingSelectionPreference: string;
  thirdPartyAccountNumber: string;
  insuranceCost: string | number;
  requiresDeliveryConfirmation: string;
  confirmationType: string;
  shipToCompanyName: string;
  shipToFirstName: string;
  shipToLastName: string;
  shipToAddress1: string;
  shipToAddress2: string;
  shipToCity: string;
  shipToStateID: string | number;
  shipToZip: string;
  shipToCountryID: string | number;
  shipToPhone: string;
}

// Define interfaces for each payload type that needs specific typing

interface ScannedItemMappingPayload {
  scannedItem: string;
  results: OrderItemScanResponse;
}

interface ReshipPropertiesPayload {
  [key: string]: any;
}

interface FulfillmentCacheEntry {
  data: any;
  fetchedOn: string;
}

interface SetFulfillmentCachePayload {
  cacheKey: string;
  data: any;
}

interface OrderState {
  selectedOrder: ProcessOrderAPI | null;
  originalItems: Record<string, OrderItemAPI>;
  itemsRemaining: Record<string, OrderItemAPI>;
  itemsCompleted: Record<string, OrderItemAPI>;
  physicalItems: Record<string, OrderPhysicalItemAPI[]>;
  inventoryPool: InventoryPool;
  skusAllowingSerialOnOutbound: string[];
  packages: ProcessingPackagesState;
  upcSkuObj: Record<string, string>;
  hasUnsealedPackage: boolean;
  hasUnsealedPallet: boolean;
  completedPackagesCount: number;
  completedItemsQty: number;
  packageEditId: any | null;
  rateId: any | null;
  canOnlyShip: boolean;
  isReship: boolean;
  hasPallets: boolean;
  shouldPromptDocQty: boolean;
  orderImportCompleteAlertPayload: FileImportCompleteMessage | null;
  allocateOrderInventoryCompletedPayload: AllocateOrderInventoryCompletedMessage | null;
  reshipState: ReshipState;
  palletId?: number | null;
  workstationId?: number | null;
  scannedOrderItemMapping: Record<string, OrderItemScanResponse>;
  fulfillmentScheduleCache: Record<string, FulfillmentCacheEntry>;
  fulfillmentProgressCache: Record<string, FulfillmentCacheEntry>;
  fulfillmentExpressShipmentsCache: Record<string, FulfillmentCacheEntry>;
  fulfillmentOpenOrdersPerCustomerCache: Record<string, FulfillmentCacheEntry>;
}

const initialState: OrderState = {
  selectedOrder: null,
  originalItems: {},
  itemsRemaining: {},
  itemsCompleted: {},
  physicalItems: {},
  inventoryPool: {},
  skusAllowingSerialOnOutbound: [],
  packages: {},
  upcSkuObj: {},
  hasUnsealedPackage: false,
  hasUnsealedPallet: false,
  completedPackagesCount: 0,
  completedItemsQty: 0,
  packageEditId: null,
  rateId: null,
  canOnlyShip: false,
  isReship: false,
  hasPallets: false,
  shouldPromptDocQty: false,
  orderImportCompleteAlertPayload: null,
  allocateOrderInventoryCompletedPayload: null,
  palletId: null,
  workstationId: null,
  scannedOrderItemMapping: {},
  fulfillmentScheduleCache: {},
  fulfillmentProgressCache: {},
  fulfillmentExpressShipmentsCache: {},
  fulfillmentOpenOrdersPerCustomerCache: {},
  reshipState: {
    // flags if the default values were overridden
    hasOverrideCarrierInfo: false,
    hasOverrideShipToInfo: false,
    reshipReturnReason: '',
    overrideShouldChargeToCustomer: false,
    warehouseID: '',
    carrierRequestedName: '',
    carrierID: '',
    carrierServiceCode: '',
    fulfillmentType: '',
    shippingSelectionPreference: '',
    thirdPartyAccountNumber: '',
    insuranceCost: '',
    requiresDeliveryConfirmation: 'no',
    confirmationType: '',
    shipToCompanyName: '',
    shipToFirstName: '',
    shipToLastName: '',
    shipToAddress1: '',
    shipToAddress2: '',
    shipToCity: '',
    shipToStateID: '',
    shipToZip: '',
    shipToCountryID: '',
    shipToPhone: '',
  },
};

const orders = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setSelectedOrder(state, action: PayloadAction<ProcessOrderAPI>) {
      state.selectedOrder = action.payload;
    },
    setOriginalItems(
      state,
      action: PayloadAction<Record<string, OrderItemAPI>>,
    ) {
      state.originalItems = action.payload;
    },
    setItemsRemaining(
      state,
      action: PayloadAction<Record<string, OrderItemAPI>>,
    ) {
      state.itemsRemaining = action.payload;
    },
    setItemsCompleted(
      state,
      action: PayloadAction<Record<string, OrderItemAPI>>,
    ) {
      const completedItems = action.payload;
      state.itemsCompleted = completedItems;
      // set the completed QTY that shows in the items tab
      if (completedItems) {
        const itemsQty = Object.keys(completedItems).reduce(
          (acc, cur) => acc + (completedItems[cur].quantity ?? 0),
          0,
        );
        state.completedItemsQty = itemsQty;
      }
    },
    setPackages(state, action: PayloadAction<ProcessingPackagesState>) {
      state.packages = action.payload;
    },
    setPhysicalItems(
      state,
      action: PayloadAction<Record<string, OrderPhysicalItemAPI[]>>,
    ) {
      state.physicalItems = action.payload;
    },
    setInventoryPool(state, action: PayloadAction<InventoryPool>) {
      state.inventoryPool = action.payload;
    },
    setSkusAllowingSerialOnOutbound(state, action: PayloadAction<string[]>) {
      state.skusAllowingSerialOnOutbound = action.payload;
    },
    setUpcSkuObj(state, action: PayloadAction<Record<string, string>>) {
      state.upcSkuObj = action.payload;
    },
    setHasUnsealedPackage(state, action: PayloadAction<boolean>) {
      state.hasUnsealedPackage = action.payload;
    },
    setHasUnsealedPallet(state, action: PayloadAction<boolean>) {
      state.hasUnsealedPallet = action.payload;
    },
    setCompletedPackagesCount(state, action: PayloadAction<number>) {
      state.completedPackagesCount = action.payload;
    },
    setPackageEditId(state, action: PayloadAction<any>) {
      state.packageEditId = action.payload;
    },
    setOrderRateId(state, action: PayloadAction<any>) {
      state.rateId = action.payload;
    },
    setCanOnlyShip(state, action: PayloadAction<boolean>) {
      state.canOnlyShip = action.payload;
    },
    setIsReship(state, action: PayloadAction<boolean>) {
      state.isReship = action.payload;
    },
    updateReshipProperties(
      state,
      action: PayloadAction<ReshipPropertiesPayload>,
    ) {
      state.reshipState = { ...state.reshipState, ...action.payload };
    },
    setPalletId(state, action: PayloadAction<number | null>) {
      state.palletId = action.payload;
    },
    setWorkstationId(state, action: PayloadAction<number | null>) {
      state.workstationId = action.payload;
    },
    setShouldPromptDocQty(state, action: PayloadAction<boolean>) {
      state.shouldPromptDocQty = action.payload;
    },
    setHasPallets(state, action: PayloadAction<boolean>) {
      state.hasPallets = action.payload;
    },
    resetOrderFields(state) {
      // https://stackoverflow.com/a/66505785
      Object.assign(state, initialState);
    },
    setOrderImportCompleteAlertPayload: (
      state,
      action: PayloadAction<FileImportCompleteMessage | null>,
    ) => {
      state.orderImportCompleteAlertPayload = action.payload;
    },
    setAllocateOrderInventoryCompletedPayload: (
      state,
      action: PayloadAction<AllocateOrderInventoryCompletedMessage | null>,
    ) => {
      state.allocateOrderInventoryCompletedPayload = action.payload;
    },
    setScannedOrderItemMapping: (
      state,
      action: PayloadAction<ScannedItemMappingPayload>,
    ) => {
      const scannedItem = action.payload.scannedItem;
      const results = action.payload.results;
      state.scannedOrderItemMapping[scannedItem] = results;
    },
    setFulfillmentScheduleCache: (
      state,
      action: PayloadAction<SetFulfillmentCachePayload>,
    ) => {
      state.fulfillmentScheduleCache[action.payload.cacheKey] = {
        data: action.payload.data,
        fetchedOn: formatISO(new Date()),
      };
    },
    setFulfillmentProgressCache: (
      state,
      action: PayloadAction<SetFulfillmentCachePayload>,
    ) => {
      state.fulfillmentProgressCache[action.payload.cacheKey] = {
        data: action.payload.data,
        fetchedOn: formatISO(new Date()),
      };
    },
    setFulfillmentExpressShipmentsCache: (
      state,
      action: PayloadAction<SetFulfillmentCachePayload>,
    ) => {
      state.fulfillmentExpressShipmentsCache[action.payload.cacheKey] = {
        data: action.payload.data,
        fetchedOn: formatISO(new Date()),
      };
    },
    setFulfillmentOpenOrdersPerCustomerCache: (
      state,
      action: PayloadAction<SetFulfillmentCachePayload>,
    ) => {
      state.fulfillmentOpenOrdersPerCustomerCache[action.payload.cacheKey] = {
        data: action.payload.data,
        fetchedOn: formatISO(new Date()),
      };
    },
    invalidateFulfillmentCache: (state) => {
      state.fulfillmentScheduleCache = {};
      state.fulfillmentProgressCache = {};
      state.fulfillmentExpressShipmentsCache = {};
      state.fulfillmentOpenOrdersPerCustomerCache = {};
    },
    /**
     * Sets multiple order processing fields in a single dispatch to avoid
     * redundant middleware passes and subscriber notifications.
     */
    bulkSetOrderProcessingState(
      state,
      action: PayloadAction<
        Partial<
          Pick<
            OrderState,
            | 'originalItems'
            | 'itemsRemaining'
            | 'itemsCompleted'
            | 'physicalItems'
            | 'inventoryPool'
            | 'skusAllowingSerialOnOutbound'
            | 'upcSkuObj'
            | 'packages'
            | 'hasUnsealedPackage'
          >
        >
      >,
    ) {
      Object.assign(state, action.payload);
      // Recompute completedItemsQty when itemsCompleted is set
      if (action.payload.itemsCompleted) {
        const completedItems = action.payload.itemsCompleted;
        state.completedItemsQty = Object.keys(completedItems).reduce(
          (acc, cur) => acc + (completedItems[cur].quantity ?? 0),
          0,
        );
      }
    },
  },
});

export const {
  setSelectedOrder,
  setOriginalItems,
  setHasUnsealedPackage,
  setHasUnsealedPallet,
  setItemsCompleted,
  setPackages,
  setPhysicalItems,
  setInventoryPool,
  setSkusAllowingSerialOnOutbound,
  setItemsRemaining,
  setUpcSkuObj,
  setCompletedPackagesCount,
  setPackageEditId,
  setOrderRateId,
  setPalletId,
  setWorkstationId,
  resetOrderFields,
  setCanOnlyShip,
  setIsReship,
  updateReshipProperties,
  setShouldPromptDocQty,
  setHasPallets,
  setOrderImportCompleteAlertPayload,
  setAllocateOrderInventoryCompletedPayload,
  setScannedOrderItemMapping,
  setFulfillmentScheduleCache,
  setFulfillmentProgressCache,
  setFulfillmentExpressShipmentsCache,
  setFulfillmentOpenOrdersPerCustomerCache,
  invalidateFulfillmentCache,
  bulkSetOrderProcessingState,
} = orders.actions;

export const ordersReducer = orders.reducer;
